import Loader from "../../../components/Loader"; // Adjust path if needed

const MarketMetrics = ({ loading, isSidebarCollapsed }) => {
    if (loading) return <Loader />;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(amount || 0);
    };

    // Mock Data (Replace with real API data later)
    const metrics = {
        avgContractPrice: 2452000,
        totalContractValue: 68656000,
        dealsCount: 28,
        avgCapRate: "6.42",
        capRateRange: "3.80% – 9.15%",
        avgAskingPricePercent: "97.3",
        askingPriceRange: "82.0% – 110.5%",
        avgLeaseTerm: "8.2",
        leaseTermRange: "1 – 25 yrs",
    };

    const leaseTypeMix = [
        { type: "NNN", percent: 72, color: "#15803d" },
        { type: "Gross", percent: 18, color: "#4ade80" },
        { type: "Modified", percent: 10, color: "#86efac" },
    ];

    return (
        <div className="light-bg-L pb-3  px-4 py-4">
            <div className=" light-bg-L">

                <div className="my-3">
                    <p className="head-2 dark-H pb-1">Market Metrics</p>
                    <p className="dark-M body-N">Analyze closed and under contract deal financials and lease terms</p>
                </div>

                {/* Top Metrics Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 mb-8 items-stretch">

                    {/* Left Section - 4 Cards */}
                    <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Avg Contract Price */}
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                            <p className="body-N text-gray-600 text-sm">Avg Contract Price</p>
                            <p className="head-2 dark-H mt-2 text-[32px]">
                                {formatCurrency(metrics.avgContractPrice)}
                            </p>
                            <div className="mt-2 space-y-1">
                                <p className="body-S text-gray-500">Total: {formatCurrency(metrics.totalContractValue)}</p>
                                <p className="body-S text-gray-500">Deals: {metrics.dealsCount}</p>
                            </div>
                        </div>

                        {/* Avg Contract Cap Rate */}
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                            <p className="body-N text-gray-600 text-sm">Avg Contract Cap Rate</p>
                            <p className="head-2 dark-H mt-2 text-[32px]">{metrics.avgCapRate}%</p>
                            <div className="mt-2 space-y-1">
                                <p className="body-S text-gray-500">Range: {metrics.capRateRange}</p>
                                <p className="body-S text-gray-500">Deals: {metrics.dealsCount}</p>
                            </div>
                        </div>

                        {/* Avg % of Asking Price */}
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                            <p className="body-N text-gray-600 text-sm">Avg % of Asking Price</p>
                            <p className="head-2 dark-H mt-2 text-[32px]">{metrics.avgAskingPricePercent}%</p>
                            <div className="mt-2 space-y-1">
                                <p className="body-S text-gray-500">Range: {metrics.askingPriceRange}</p>
                                <p className="body-S text-gray-500">Deals: {metrics.dealsCount}</p>
                            </div>
                        </div>

                        {/* Avg Lease Term Remaining */}
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                            <p className="body-N text-gray-600 text-sm">Avg Lease Term Remaining</p>
                            <p className="head-2 dark-H mt-2 text-[32px]">{metrics.avgLeaseTerm} yrs</p>
                            <div className="mt-2 space-y-1">
                                <p className="body-S text-gray-500">Range: {metrics.leaseTermRange}</p>
                                <p className="body-S text-gray-500">Deals: {metrics.dealsCount}</p>
                            </div>
                        </div>
                    </div>

                    {/* Right Section - Perfect Height Match */}
                    <div className="lg:col-span-3 bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col h-full">

                        <p className="body-N text-gray-600 mb-4">Lease Type Mix</p>

                        {/* Chart Container - Takes available space */}
                        <div className="flex justify-center flex-1 items-center py-2">
                            <div className="relative w-28 h-28">
                                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-12">
                                    <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="10" />
                                    <circle
                                        cx="50" cy="50" r="45"
                                        fill="none"
                                        stroke="#15803d"
                                        strokeWidth="10"
                                        strokeDasharray={`${72 * 2.83} ${100 * 2.83}`}
                                        strokeDashoffset="25"
                                    />
                                </svg>
                            </div>
                        </div>

                        {/* Legend */}
                        <div className="space-y-1.5 text-sm mt-2">
                            {leaseTypeMix.map((item, i) => (
                                <div key={i} className="flex justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                                        <span>{item.type}</span>
                                    </div>
                                    <span className="font-medium">{item.percent}%</span>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <p className="body-S text-gray-500 mt-auto pt-4 text-center">
                            Deals: {metrics.dealsCount}
                        </p>
                    </div>
                </div>


                {/* Deal Metrics Table */}
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <p className="head-4 dark-H">
                            Deal Metrics <span className="text-gray-500">28 deals</span>
                        </p>

                        <button className="text-green-H body-S flex items-center gap-1 hover:underline">
                            Customize Columns
                        </button>
                    </div>

                    <div className="bg-white rounded-xl border overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr className="uppercase body-N dark-M text-left">
                                    <th className="p-4">Property Name</th>
                                    <th className="p-4">Lease Type</th>
                                    <th className="p-4">Lease Term Remaining</th>
                                    <th className="p-4">NOI</th>
                                    <th className="p-4">Contract Price</th>
                                    <th className="p-4">Contract Cap Rate</th>
                                    <th className="p-4">% of Asking Price</th>
                                </tr>
                            </thead>

                            <tbody className="body-N dark-M divide-y">
                                {[
                                    {
                                        name: "Dollar General – Madison, GA",
                                        noi: 153000,
                                        price: 2319000,
                                        cap: "6.59%",
                                        asking: "96.2%",
                                        term: "9.3 yrs",
                                        type: "NNN",
                                    },
                                    {
                                        name: "PNC Bank – Missouri City, TX",
                                        noi: 165000,
                                        price: 2460000,
                                        cap: "6.71%",
                                        asking: "98.4%",
                                        term: "7.1 yrs",
                                        type: "NNN",
                                    },
                                    {
                                        name: "DG – East Palestine, OH",
                                        noi: 95000,
                                        price: 1354600,
                                        cap: "7.02%",
                                        asking: "94.1%",
                                        term: "5.0 yrs",
                                        type: "NNN",
                                    },
                                    {
                                        name: "McDonald's – Harlan, KY",
                                        noi: 175000,
                                        price: 2600000,
                                        cap: "6.73%",
                                        asking: "99.5%",
                                        term: "10.0 yrs",
                                        type: "NNN",
                                    },
                                ].map((deal, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="p-4 font-medium">{deal.name}</td>
                                        <td className="p-4">{deal.type}</td>
                                        <td className="p-4">{deal.term}</td>
                                        <td className="p-4">{formatCurrency(deal.noi)}</td>
                                        <td className="p-4">{formatCurrency(deal.price)}</td>
                                        <td className="p-4">{deal.cap}</td>
                                        <td className="p-4">{deal.asking}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MarketMetrics;